import { useMemo } from 'react';
import type { ShopperProducts } from '@/scapi';
import type { GalleryImage } from '@/components/image-gallery';
import { findImageGroupBy } from '@/lib/image-groups-utils';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { toGalleryThumbnailSrc } from '@/lib/images/gallery-sizes';
import { PDP_GALLERY_VIEW_TYPES } from '@/lib/product-image-view-types';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

interface UseProductImagesProps {
    product: ShopperProducts.schemas['Product'];
    selectedAttributes?: Record<string, string>;
    viewType?: string;
}

interface UseProductImagesReturn {
    galleryImages: GalleryImage[];
}

/**
 * Helper function to get default images from image groups
 * @param imageGroups - The image groups array
 * @param viewType - The view type to filter by
 * @returns Array of images for the specified view type
 */
const getDefaultImages = (
    imageGroups: ShopperProducts.schemas['ImageGroup'][] | undefined,
    viewType: string
): ShopperProducts.schemas['Image'][] => {
    return imageGroups?.find((group) => group.viewType === viewType)?.images || [];
};

const getImagesForViewType = (
    imageGroups: ShopperProducts.schemas['ImageGroup'][] | undefined,
    viewType: string,
    selectedAttributes?: Record<string, string>
): ShopperProducts.schemas['Image'][] => {
    if (!selectedAttributes || Object.keys(selectedAttributes).length === 0) {
        return getDefaultImages(imageGroups, viewType);
    }

    const imageGroup = findImageGroupBy(imageGroups || [], {
        viewType,
        selectedVariationAttributes: selectedAttributes,
    });

    return imageGroup?.images || getDefaultImages(imageGroups, viewType);
};

/**
 * Manages product images based on variation attributes with automatic fallbacks.
 *
 * @example Basic usage in ProductView
 * ```tsx
 * const [selectedAttributes, setSelectedAttributes] = useState({});
 * const { galleryImages } = useProductImages({
 *   product,
 *   selectedAttributes,
 * });
 * return <ImageGallery images={galleryImages} />;
 * ```
 *
 * @example With specific view type
 * ```tsx
 * const { galleryImages } = useProductImages({
 *   product,
 *   selectedAttributes: { color: 'red', size: 'M' },
 *   viewType: 'LAB'
 * });
 * ```
 *
 * @param props - Configuration object
 * @param props.product - Product with image groups
 * @param props.selectedAttributes - Selected variation attributes
 * @param props.viewType - Optional single image view type. Omit it for the ordered PDP gallery.
 * @returns Gallery images and utility functions
 */
export function useProductImages({
    product,
    selectedAttributes,
    viewType,
}: UseProductImagesProps): UseProductImagesReturn {
    const config = useConfig();

    // Explicit view types are used by labels and event/course layouts. The default
    // physical PDP gallery concatenates each role while preserving group image order.
    const filteredImages = useMemo(() => {
        if (viewType) {
            return getImagesForViewType(product.imageGroups, viewType, selectedAttributes);
        }

        return PDP_GALLERY_VIEW_TYPES.flatMap((galleryViewType) =>
            getImagesForViewType(product.imageGroups, galleryViewType, selectedAttributes)
        );
    }, [product.imageGroups, selectedAttributes, viewType]);

    // Transform Commerce SDK images to GalleryImage format
    const galleryImages: GalleryImage[] = useMemo(() => {
        if (!filteredImages || filteredImages.length === 0) {
            return [];
        }

        return filteredImages.map((image: ShopperProducts.schemas['Image']) => {
            const optimizedImageUrl = toImageUrl({ image, config }) || '';
            return {
                src: optimizedImageUrl,
                alt: image.alt || product.name || '',
                thumbSrc: toGalleryThumbnailSrc(optimizedImageUrl),
            };
        });
    }, [filteredImages, product.name, config]);

    return {
        // Transformed images
        /** Array of images formatted for gallery display, filtered by selected attributes */
        galleryImages,
    };
}
